
import React from 'react';
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, CheckCircle, XCircle, CreditCard } from "lucide-react";
import UserRole from './UserRole';
import UserActions from './UserActions';
import { Badge } from "@/components/ui/badge";
import { useBillingPlans } from '@/hooks/useBillingPlans';

type User = {
  id: string;
  full_name: string | null;
  email: string;
  role: string;
  created_at: string;
  organization?: string | null;
  website_url?: string | null;
  is_active: boolean;
  email_confirmed?: boolean;
  plan?: string;
};

type UserRowProps = {
  user: User;
  isDeleting: boolean;
  onEdit: (userId: string, email: string) => void;
  onSetAdminRole: (email: string) => Promise<void>;
  onDeleteUser: (userId: string, email: string) => Promise<void>;
  onToggleActive: (userId: string, isActive: boolean) => Promise<void>;
};

const UserRow: React.FC<UserRowProps> = ({ 
  user, 
  isDeleting, 
  onEdit, 
  onSetAdminRole, 
  onDeleteUser, 
  onToggleActive 
}) => {
  const { billingPlans } = useBillingPlans();
  
  // Get readable plan name
  const getPlanBadge = (planId: string = 'free') => {
    const plan = billingPlans.find(p => p.id === planId);
    
    if (plan) {
      return (
        <Badge className={plan.is_enterprise ? "bg-purple-500 flex items-center gap-1" : "bg-blue-500 flex items-center gap-1"}>
          <CreditCard className="h-3 w-3" /> {plan.name}
        </Badge>
      );
    }
    
    switch(planId) {
      case 'pro':
        return <Badge className="bg-blue-500 flex items-center gap-1"><CreditCard className="h-3 w-3" /> Pro</Badge>;
      case 'enterprise':
        return <Badge className="bg-purple-500 flex items-center gap-1"><CreditCard className="h-3 w-3" /> Enterprise</Badge>;
      default:
        return <Badge className="bg-gray-500 flex items-center gap-1"><CreditCard className="h-3 w-3" /> Free</Badge>;
    }
  };

  return (
    <TableRow key={user.id} className={!user.is_active ? "opacity-60" : ""}>
      <TableCell className="font-medium">{user.full_name || 'Non défini'}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>
        {user.email_confirmed ? (
          <span className="flex items-center text-green-600">
            <CheckCircle className="h-4 w-4 mr-1" />
            Oui
          </span>
        ) : (
          <span className="flex items-center text-amber-600">
            <XCircle className="h-4 w-4 mr-1" />
            Non
          </span>
        )}
      </TableCell>
      <TableCell><UserRole role={user.role} /></TableCell>
      <TableCell>{getPlanBadge(user.plan)}</TableCell>
      <TableCell>
        <span className={`px-2 py-1 rounded text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {user.is_active ? 'Actif' : 'Inactif'}
        </span>
      </TableCell>
      <TableCell>
        {new Date(user.created_at).toLocaleDateString('fr-FR')}
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onEdit(user.id, user.email)}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Éditer
          </Button>
          <UserActions 
            userId={user.id}
            email={user.email}
            role={user.role}
            isActive={user.is_active}
            isDeleting={isDeleting}
            onSetAdminRole={onSetAdminRole}
            onDeleteUser={onDeleteUser}
            onToggleActive={onToggleActive}
          />
        </div>
      </TableCell>
    </TableRow>
  );
};

export default UserRow;
